<script setup lang="ts">
// iTorgo's own category artwork. One vector object per category, drawn in a
// shared style (3px ink outline, round joins, one flat accent), composed two
// ways and picked in Admin > Design:
//   - 'shyrdak': the object on a tinted tile with a shyrdak ram-horn
//     (kochkor muyuz) motif in the corner and a zigzag band along the base;
//   - 'object':  the object on a soft coloured disc, on a plain card.
// No external images: each tile is a few KB of inline SVG, crisp at any size,
// and adding a category means adding one object + one palette entry below.
import { computed } from 'vue'
import { paletteFor, type ArtTone } from '@/data/categoryArtPalettes'
import { useThemeStore } from '@/stores/theme'

interface Props {
  slug: string
  variant?: 'shyrdak' | 'object'
  // 'portrait' tiles (4:5, homepage) centre the object near the top;
  // 'wide' banners (16:10, categories page, menu) put it on the right.
  layout?: 'portrait' | 'wide'
  // Override the site palette (e.g. a preview); defaults to the live theme.
  tone?: ArtTone
}
const props = withDefaults(defineProps<Props>(), { variant: 'shyrdak', layout: 'portrait' })

const themeStore = useThemeStore()
const p = computed(() => paletteFor(props.slug, props.tone || {
  ink: themeStore.theme.inkColor || '#14171F',
  ground: themeStore.theme.backgroundColor || '#F7F6F2',
}))
// Stable per-component id so several tiles on one page don't share <pattern> ids.
const uid = `ca-${Math.random().toString(36).slice(2, 9)}`
</script>

<template>
  <div
    class="absolute inset-0 overflow-hidden"
    :style="{ background: variant === 'shyrdak' ? p.bg : 'rgb(var(--color-surface))' }"
    aria-hidden="true"
  >
    <!-- Shyrdak decoration: ram-horn motifs in the top-right corner and a
         zigzag band along the base. -->
    <svg v-if="variant === 'shyrdak'" class="absolute inset-0 h-full w-full" :viewBox="layout === 'wide' ? '0 0 200 125' : '0 0 120 150'" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern :id="`${uid}-zig`" width="12" height="10" patternUnits="userSpaceOnUse">
          <path d="M0 8 L6 2 L12 8" fill="none" :stroke="p.ink" stroke-width="1.6" stroke-linejoin="round" />
        </pattern>
      </defs>
      <g fill="none" :stroke="p.ink" stroke-width="1.8" stroke-linecap="round" opacity="0.22" :transform="layout === 'wide' ? 'translate(80 0)' : undefined">
        <path d="M100 26 V17 C100 10 93 8 89 11 C86 14 88 18 91 17 M100 17 C100 10 107 8 111 11 C114 14 112 18 109 17" />
        <path d="M84 12 V6 C84 1 79 0 76 2 C74 4 75 7 77 6 M84 6 C84 1 89 0 92 2 C94 4 93 7 91 6" />
        <path d="M114 42 V36 C114 31 109 30 106 32 C104 34 105 37 107 36 M114 36 C114 31 119 30 122 32" />
      </g>
      <rect x="0" :y="layout === 'wide' ? 111 : 134" width="200" height="10" :fill="`url(#${uid}-zig)`" opacity="0.2" />
      <line x1="0" :y1="layout === 'wide' ? 108 : 131" x2="200" :y2="layout === 'wide' ? 108 : 131" :stroke="p.ink" stroke-width="1.2" opacity="0.14" />
    </svg>

    <!-- Object variant: a soft disc behind the object -->
    <div
      v-else
      class="absolute aspect-square rounded-full"
      :class="layout === 'wide' ? 'right-[5%] top-1/2 h-[86%] -translate-y-1/2' : 'left-1/2 top-[5%] w-[76%] -translate-x-1/2'"
      :style="{ background: p.bg }"
    />

    <!-- The object itself (shared by both variants) -->
    <svg
      class="absolute"
      :class="layout === 'wide'
        ? 'right-[7%] top-1/2 h-[80%] w-auto aspect-square -translate-y-1/2'
        : ['left-1/2 -translate-x-1/2', variant === 'shyrdak' ? 'top-[8%] w-[74%]' : 'top-[10%] w-[66%]']"
      viewBox="0 0 120 120"
      fill="none"
      :stroke="p.ink"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <!-- Electronics: phone with a signal -->
      <template v-if="slug === 'electronics'">
        <rect x="40" y="18" width="40" height="76" rx="8" :fill="p.soft" />
        <rect x="46" y="28" width="28" height="52" rx="3" :fill="p.accent" />
        <path d="M55 23 H65" />
        <circle cx="60" cy="87" r="2.2" :fill="p.ink" stroke="none" />
        <path d="M86 26 a10 10 0 0 1 8 8 M88 18 a18 18 0 0 1 14 14" />
        <path d="M52 44 H68 M52 52 H64" stroke-width="2.4" opacity="0.55" />
      </template>

      <!-- Vehicles: car, side view -->
      <template v-else-if="slug === 'vehicles'">
        <path d="M20 76 H100 a4 4 0 0 0 4 -4 V64 a7 7 0 0 0 -6 -7 L84 54 L72 42 a9 9 0 0 0 -6 -3 H46 a9 9 0 0 0 -7 4 L30 55 L22 57 a6 6 0 0 0 -6 6 V72 a4 4 0 0 0 4 4 Z" :fill="p.accent" />
        <path d="M44 55 L50 45 H60 V55 Z M66 45 H70 L79 55 H66 Z" :fill="p.soft" />
        <circle cx="40" cy="77" r="10" :fill="p.ink" />
        <circle cx="40" cy="77" r="4" :fill="p.soft" stroke="none" />
        <circle cx="82" cy="77" r="10" :fill="p.ink" />
        <circle cx="82" cy="77" r="4" :fill="p.soft" stroke="none" />
        <path d="M100 62 H104" />
      </template>

      <!-- Real estate: house -->
      <template v-else-if="slug === 'real-estate'">
        <rect x="76" y="30" width="9" height="16" :fill="p.soft" />
        <path d="M30 56 V92 H90 V56" :fill="p.soft" />
        <path d="M22 60 L60 28 L98 60" :fill="p.accent" />
        <rect x="53" y="70" width="14" height="22" rx="1.5" :fill="p.accent" />
        <rect x="36" y="64" width="11" height="10" rx="1.5" :fill="p.bg" />
        <rect x="73" y="64" width="11" height="10" rx="1.5" :fill="p.bg" />
        <path d="M18 92 H102" />
      </template>

      <!-- Livestock: sheep -->
      <template v-else-if="slug === 'livestock'">
        <path d="M48 80 V94 M58 82 V94 M72 82 V94 M82 80 V94" stroke-width="4" />
        <path d="M40 72 q-9 -2 -7 -11 q-2 -10 8 -12 q2 -10 13 -9 q6 -8 15 -4 q9 -2 13 6 q10 1 9 11 q6 8 -1 14 q1 10 -10 9 q-5 6 -13 4 q-8 5 -15 0 q-10 3 -12 -8 Z" :fill="p.accent" />
        <ellipse cx="94" cy="56" rx="9" ry="11" :fill="p.ink" />
        <path d="M86 50 q-6 -4 -9 1" :fill="p.ink" />
        <circle cx="97" cy="54" r="1.6" :fill="p.soft" stroke="none" />
      </template>

      <!-- Jewelry: ring with a stone -->
      <template v-else-if="slug === 'jewelry'">
        <circle cx="60" cy="72" r="22" stroke-width="6" :stroke="p.ink" />
        <circle cx="60" cy="72" r="22" stroke-width="2" :stroke="p.soft" />
        <path d="M46 40 H74 L81 49 L60 70 L39 49 Z" :fill="p.accent" />
        <path d="M39 49 H81 M53 40 L50 49 L60 70 L70 49 L67 40" stroke-width="2" />
        <path d="M90 28 v8 M86 32 h8 M28 36 v6 M25 39 h6" stroke-width="2.4" />
      </template>

      <!-- Art & antiques: framed Ala-Too landscape -->
      <template v-else-if="slug === 'art' || slug === 'antiques'">
        <path d="M52 22 L60 14 L68 22" />
        <rect x="24" y="22" width="72" height="62" rx="3" :fill="p.soft" />
        <rect x="31" y="29" width="58" height="48" rx="1.5" :fill="p.bg" />
        <path d="M31 77 L47 53 L57 66 L68 50 L89 77 Z" :fill="p.accent" />
        <path d="M44 58 L47 53 L50 58" stroke-width="2" :stroke="p.soft" />
        <circle cx="78" cy="40" r="5" :fill="p.soft" />
        <path d="M40 92 L46 84 M80 92 L74 84" />
      </template>

      <!-- Machinery: tractor -->
      <template v-else-if="slug === 'machinery'">
        <path d="M36 50 V36" stroke-width="4" />
        <path d="M58 30 H82 L86 60 H58 Z" :fill="p.soft" />
        <path d="M63 36 H79 L81 50 H63 Z" :fill="p.bg" />
        <rect x="26" y="50" width="40" height="20" rx="3" :fill="p.accent" />
        <circle cx="76" cy="76" r="17" :fill="p.ink" />
        <circle cx="76" cy="76" r="6" :fill="p.accent" stroke="none" />
        <circle cx="36" cy="82" r="11" :fill="p.ink" />
        <circle cx="36" cy="82" r="4" :fill="p.accent" stroke="none" />
      </template>

      <!-- Home goods: sofa -->
      <template v-else-if="slug === 'home-goods'">
        <rect x="28" y="42" width="64" height="28" rx="9" :fill="p.accent" />
        <rect x="22" y="62" width="76" height="18" rx="6" :fill="p.soft" />
        <rect x="16" y="56" width="14" height="28" rx="6" :fill="p.accent" />
        <rect x="90" y="56" width="14" height="28" rx="6" :fill="p.accent" />
        <path d="M26 84 V92 M94 84 V92" stroke-width="4" />
        <path d="M60 46 V66" stroke-width="2" opacity="0.5" />
      </template>

      <!-- Anything new: a stacked-boxes placeholder in the same style -->
      <template v-else>
        <rect x="32" y="50" width="56" height="40" rx="4" :fill="p.accent" />
        <rect x="42" y="28" width="36" height="24" rx="4" :fill="p.soft" />
      </template>
    </svg>
  </div>
</template>
